import { type ApiSchemas, call } from '@rental/contracts/client'
import { Button, Field } from '@rental/ui'
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { api } from '../api'
import { Section } from '../components/Section'
import { SubPanel } from '../components/SubPanel'
import {
  FIELD,
  FIELD_ROW,
  FieldPair,
  PAIRED_FIELD,
  ProblemNotice,
  StaffInput,
} from '../components/ui'
import { noImplicitSubmit } from '../forms'
import { useLocale } from '../i18n'
import {
  emptyNotice,
  NOTICE_FIELDS,
  type NoticeDraft,
  type NoticeField,
  noticeBody,
  noticeDraftOf,
} from './fines'
import { useSend } from './send'

type StaffFine = ApiSchemas['StaffFine']

const INPUT: Readonly<
  Record<NoticeField, { type: 'text' | 'date' | 'time'; inputMode?: 'decimal'; max?: number }>
> = {
  authority: { type: 'text', max: 200 },
  noticeNumber: { type: 'text', max: 60 },
  violatedOn: { type: 'date' },
  violatedAt: { type: 'time' },
  place: { type: 'text', max: 300 },
  plate: { type: 'text', max: 20 },
  amount: { type: 'text', inputMode: 'decimal', max: 20 },
  notifiedOn: { type: 'date' },
}

/**
 * The notice typed as printed: registered new, or corrected while no rental
 * is named yet. The server answers with the fine as it then stands, matched
 * already when a single rental had the car at that minute. A form named by
 * its heading: on a pane of its own on the register (`pane`), sunk into the
 * notice's pane on the fine's page (`well`, UI-2e).
 */
export function FineForm({
  correcting,
  surface,
  onDone,
  onCancel,
}: {
  /** The fine to correct; none to register a new notice. */
  correcting?: StaffFine
  surface: 'pane' | 'well'
  onDone: (fine: StaffFine) => void
  onCancel: () => void
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.fines.form
  const titleId = useId()
  const [draft, setDraft] = useState<NoticeDraft>(() =>
    correcting ? noticeDraftOf(correcting, locale) : emptyNotice(),
  )
  const [invalid, setInvalid] = useState<readonly NoticeField[]>([])
  // Each press that found the notice wrong: the first field it found wrong takes the focus
  // (UI-2g). Eight fields typed from a printed notice are checked when the counter presses,
  // not one by one while they type.
  const [refused, setRefused] = useState(0)
  const form = useRef<HTMLFormElement>(null)
  useEffect(() => {
    if (refused > 0) form.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
  }, [refused])
  const { send, busy, problem } = useSend()

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    const outcome = noticeBody(draft)
    if (!outcome.ok) {
      setInvalid(outcome.invalid)
      setRefused((count) => count + 1)
      return
    }
    setInvalid([])
    const answer = await send(() =>
      correcting
        ? call(
            api.POST('/v1/staff/fines/{id}/correction', {
              params: { path: { id: correcting.id } },
              body: outcome.body,
            }),
          )
        : call(api.POST('/v1/staff/fines', { body: outcome.body })),
    )
    if (answer) onDone(answer.fine)
  }

  const title = <span id={titleId}>{correcting ? copy.titleCorrect : copy.titleNew}</span>
  /** One field of the notice, as typed on it. */
  const noticeField = (field: NoticeField, className: string) => {
    const input = INPUT[field]
    return (
      <Field
        key={field}
        className={className}
        label={copy[field]}
        hint={
          field === 'authority'
            ? copy.authorityHint
            : field === 'amount'
              ? copy.amountHint
              : undefined
        }
        error={invalid.includes(field) ? copy.errors[field] : null}
        required
      >
        {(control) => (
          <StaffInput
            {...control}
            type={input.type}
            {...(input.inputMode ? { inputMode: input.inputMode } : {})}
            {...(input.max ? { maxLength: input.max } : {})}
            autoComplete="off"
            {...(field === 'plate' ? { autoCapitalize: 'characters' } : {})}
            value={draft[field]}
            onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
          />
        )}
      </Field>
    )
  }
  const body: ReactNode = (
    <>
      <p className="text-sm text-text-muted">{copy.hint}</p>
      {invalid.length > 0 ? (
        <p role="alert" className="font-medium text-danger">
          {copy.invalid}
        </p>
      ) : null}
      <div className={FIELD_ROW}>
        {NOTICE_FIELDS.map((field) =>
          // The violation's day and its hour go together: one pair, side by side only where
          // each shows its whole value (UI-2g). Every other field stands on its own.
          field === 'violatedAt' ? null : field === 'violatedOn' ? (
            <FieldPair key="violated">
              {noticeField('violatedOn', PAIRED_FIELD)}
              {noticeField('violatedAt', PAIRED_FIELD)}
            </FieldPair>
          ) : (
            noticeField(field, FIELD)
          ),
        )}
      </div>
      <ProblemNotice problem={problem} />
      <div className="flex flex-wrap gap-3">
        <Button type="submit" variant="staff" busy={busy}>
          {correcting ? copy.save : copy.submit}
        </Button>
        <Button variant="outline" className="rounded-sm" onClick={onCancel} disabled={busy}>
          {copy.cancel}
        </Button>
      </div>
    </>
  )
  return (
    <form
      ref={form}
      onSubmit={submit}
      onKeyDown={noImplicitSubmit}
      aria-labelledby={titleId}
      noValidate
    >
      {surface === 'pane' ? (
        <Section title={title}>{body}</Section>
      ) : (
        <SubPanel title={title}>{body}</SubPanel>
      )}
    </form>
  )
}
